import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import Script from 'next/script';
import React, { useCallback, useEffect, useState } from 'react';
import { defaultLocale } from '~/locale/defaults';
import { Session } from '~/models/auth';
import { GTM_EVENTS, gtmEvent } from '~/utils/gtm-helpers';
import { useProfileTier } from '~/hooks/useProfileTier';
import { PROFILE_TIERS } from '~/constants/profileTier';

interface GoogleTagManagerProps {
    gtmID?: string;
}

const isValidGtmId = (gtmId?: string) => {
    return !!gtmId && gtmId.startsWith('GTM-');
};

function GoogleTagManager({ gtmID }: GoogleTagManagerProps) {
    const router = useRouter();
    const { data, status } = useSession();
    const session = data as Session;
    const [appInsightSessionId, setAppInsightSessionId] = useState<string | null>(null);
    const lastPageView = React.useRef('');
    const [isGtmLoaded, setGtmLoaded] = useState(false);
    const [pageReferrer, setPageReferrer] = useState<string | null>(null);
    const { profileTier } = useProfileTier();

    const onGtmScriptLoaded = useCallback(() => {
        setGtmLoaded(true);
        console.log('GTM script loaded!');
    }, []);

    const triggerPageView = useCallback(() => {
        // Dedupe pageviews by route only. Session/profile state can settle after
        // initial render and should not emit a second pageview for the same path.
        const pageViewKey = `${router.locale ?? defaultLocale}${router.asPath}`;

        // skip if the pageview event is already triggered for the current route
        if (pageViewKey === lastPageView.current) {
            return;
        }

        const userId = session?.user?.id as string;
        const userEmail = session?.user?.email as string;
        gtmEvent(
            GTM_EVENTS.PAGE_VIEW,
            window.document,
            appInsightSessionId as string,
            userId ? (profileTier == PROFILE_TIERS.T2 ? PROFILE_TIERS.T2 : 'signed in') : 'guest',
            userId,
            userEmail,
            pageReferrer ?? undefined
        );
        lastPageView.current = pageViewKey;
        console.log('GTM page view event triggered for path: ', router.asPath);
    }, [router.asPath, router.locale, appInsightSessionId, session, profileTier, pageReferrer]);

    useEffect(() => {
        if (typeof window === 'undefined' || !isValidGtmId(gtmID)) {
            return;
        }

        if (!appInsightSessionId) {
            const sessionId = sessionStorage.getItem('x-session-id') as string;
            if (sessionId) {
                setAppInsightSessionId(sessionId);
                return;
            }

            const sessionStorageChangeListener = ((event: CustomEvent) => {
                const eventValue = event?.detail?.value;
                if (event?.detail?.name === 'x-session-id' && sessionId !== eventValue) {
                    setAppInsightSessionId(eventValue);
                    window.removeEventListener(
                        'sessionStorageChange',
                        sessionStorageChangeListener
                    );
                }
            }) as EventListener;

            window.addEventListener('sessionStorageChange', sessionStorageChangeListener);
            return () => {
                window.removeEventListener('storage', sessionStorageChangeListener);
            };
        }
    }, [gtmID, appInsightSessionId]);

    useEffect(() => {
        if (
            typeof window === 'undefined' ||
            !isValidGtmId(gtmID) ||
            !router.isReady ||
            status === 'loading' ||
            !appInsightSessionId ||
            !isGtmLoaded
        ) {
            return;
        }

        const handleRouteChangeStart = () => {
            // Capture current URL as the referrer before route changes
            setPageReferrer(window.location.href);
        };

        const handleRouteChange = () => {
            triggerPageView();
        };

        // add GTM on page load
        triggerPageView();

        // capture referrer before route changes
        router.events.on('routeChangeStart', handleRouteChangeStart);
        // add GTM when page is routed via router
        router.events.on('routeChangeComplete', handleRouteChange);

        return () => {
            router.events.off('routeChangeStart', handleRouteChangeStart);
            router.events.off('routeChangeComplete', handleRouteChange);
        };
    }, [gtmID, router.isReady, isGtmLoaded, triggerPageView]);

    if (!isValidGtmId(gtmID)) {
        return null;
    }

    return (
        <>
            <Script
                id="google-tag-manager"
                strategy="afterInteractive"
                onReady={onGtmScriptLoaded}
                dangerouslySetInnerHTML={{
                    __html: `
                        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                        })(window,document,'script','dataLayer','${gtmID}');  
                    `,
                }}
            />
        </>
    );
}

export default GoogleTagManager;
